import { environmentFileUrl } from './environmentFiles.js'

/**
 * Reflection-capture artifacts for a splat (ENG-561 Phase B): a manifest plus
 * one PNG per capture.
 *
 * The manifest URL comes from the server (`files.reflections`), which is null
 * when the environment has no captures baked. This used to be computed from the
 * splat's path, which broke silently for every pre-migration environment
 * (ENG-999).
 *
 * The PNGs are resolved RELATIVE TO THE MANIFEST — `0.png` next to the manifest
 * we were handed. That is document-relative reference, the same way a glTF names
 * its .bin or a playlist names its segments: it needs no knowledge of the
 * server's storage layout, only of the manifest's own URL. Which is why the
 * server does not enumerate them.
 */

export async function reflectionManifestUrl(splatUrl) {
  return environmentFileUrl(splatUrl, 'reflections')
}

export async function reflectionCaptureUrl(splatUrl, i) {
  const manifest = await reflectionManifestUrl(splatUrl)
  if (!manifest) return null
  return captureUrlFrom(manifest, i)
}

/**
 * The i-th capture beside a manifest URL. Exported for tests and for callers
 * that already hold the manifest URL and should not re-resolve it.
 * Relative resolution via URL(), with a base so a root-relative path works.
 */
export function captureUrlFrom(manifestUrl, i) {
  const base = manifestUrl.split('?')[0]
  const resolved = new URL(`${i}.png`, `http://x${base.startsWith('/') ? '' : '/'}${base}`)
  return resolved.pathname
}

/**
 * True only when the server has a reflection manifest for this environment AND
 * it parses as one.
 *
 * The existence half is now the server's answer rather than a fetch: no
 * manifest URL means not baked. The fetch remains because a URL only tells us
 * the object is there, not that the body is intact — a truncated or
 * SPA-fallback body must be loud, not a silent stand-down.
 */
export async function reflectionManifestExists(splatUrl) {
  const url = await reflectionManifestUrl(splatUrl)
  if (!url) return false
  try {
    const res = await fetch(url)
    if (!res.ok) return false
    if (/html/.test(res.headers.get('content-type') || '')) return false
    // Read text first so a parse failure reports WHAT the body was: an HTML
    // head = SPA-fallback / proxy error page (routing), a JSON head with a
    // short length = body truncated in flight. A bare false can't separate
    // "manifest genuinely absent" from "manifest present but mangled en
    // route" — and the latter must be loud, not a silent stand-down.
    const text = await res.text()
    try {
      const j = JSON.parse(text)
      return Array.isArray(j?.captures)
    } catch {
      console.warn(`[reflections] manifest at ${url} returned 200 but unparseable body: len=${text.length} head=${JSON.stringify(text.slice(0, 60))}`)
      return false
    }
  } catch (e) {
    console.warn(`[reflections] manifest fetch failed at ${url}: ${e?.message ?? e}`)
    return false
  }
}
